// src/components/admin/AppointmentList.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPhoneAlt, FaCommentDots, FaReply, FaCheckCircle, FaSpinner } from 'react-icons/fa'; // Icons
import { format } from 'date-fns'; // For date formatting

const API_BASE = process.env.REACT_APP_API_URL;

const AppointmentList = () => {
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(''); // General success/error message

  // Function to fetch appointments
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Authentication error: Not logged in or token expired.');
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API_BASE}/api/appointments`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      // Sort appointments to show 'new' first, then 'read', then 'replied'
      const sortedAppointments = res.data.sort((a, b) => {
        const statusOrder = { 'new': 1, 'read': 2, 'replied': 3 };
        return statusOrder[a.status] - statusOrder[b.status] || new Date(b.createdAt) - new Date(a.createdAt);
      });
      setAppointments(sortedAppointments);
    } catch (err) {
      console.error('Error fetching appointments:', err.response?.data || err.message);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setMessage('❌ Failed to load appointments. Access denied: Admins only.');
      } else if (err.response?.status === 404) {
        setMessage('❌ Failed to load appointments. API route not found. Check server.js for /api/appointments.');
      } else {
        setMessage('❌ Failed to load appointments. Please check your backend connection.');
      }
    } finally {
      setLoading(false);
    }
  }, []); // API_BASE removed from dependency array

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Function to open the detail view of an appointment
  const openAppointmentDetails = async (appointment) => {
    setSelectedAppointment(appointment);
    setReplyMessage(appointment.reply || ''); // Pre-fill reply if it exists

    // If the status is 'new', mark it as 'read' on the backend
    if (appointment.status === 'new') {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_BASE}/api/appointments/mark-read/${appointment._id}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        // Update local state without refetching all, just change this item's status
        setAppointments(prev => prev.map(a =>
          a._id === appointment._id ? { ...a, status: 'read' } : a
        ));
        setSelectedAppointment(prev => ({ ...prev, status: 'read' })); // Update selected one too
      } catch (err) {
        console.error('Error marking appointment as read:', err.response?.data || err.message);
        setMessage('❌ Failed to mark appointment as read.');
      }
    }
  };

  // Function to send a reply
  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      setMessage('Please type a reply message.');
      return;
    }
    if (!selectedAppointment) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/appointments/reply/${selectedAppointment._id}`, { replyMessage }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setMessage('✅ Reply sent successfully and appointment marked as replied!');
      // Update local state
      setAppointments(prev => prev.map(a =>
        a._id === selectedAppointment._id ? { ...a, status: 'replied', reply: replyMessage } : a
      ));
      setSelectedAppointment(prev => ({ ...prev, status: 'replied', reply: replyMessage })); // Update selected one
    } catch (err) {
      console.error('Error sending reply:', err.response?.data || err.message);
      setMessage('❌ Failed to send reply. Please try again.');
    }
  };

  // Function to close the detail view
  const closeDetails = () => {
    setSelectedAppointment(null);
    setReplyMessage('');
    fetchAppointments(); // Refresh list to ensure statuses are updated and sorted
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-red-500 text-white';
      case 'read': return 'bg-yellow-500 text-white';
      case 'replied': return 'bg-green-500 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow-lg min-h-[500px]">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Appointment Management</h2>

      {message && (
        <div className={`p-3 mb-4 rounded-md ${message.startsWith('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <FaSpinner className="animate-spin text-cyan-600 text-4xl" />
          <p className="ml-3 text-gray-600">Loading appointments...</p>
        </div>
      ) : appointments.length === 0 ? (
        <p className="text-gray-600 text-center py-8">No appointments found.</p>
      ) : (
        <div className="space-y-4">
          {appointments.map(appt => (
            <div
              key={appt._id}
              className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition duration-200
                ${appt.status === 'new' ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}
                ${selectedAppointment?._id === appt._id ? 'ring-2 ring-cyan-500 border-cyan-500' : ''}
              `}
              onClick={() => openAppointmentDetails(appt)}
            >
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(appt.status)}`}>
                {appt.status.toUpperCase()}
              </div>
              <div className="flex-grow">
                <p className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaPhoneAlt className="text-cyan-600" /> {appt.name} - {appt.phone}
                </p>
                {/* Changed truncate max-w-lg to break-words */}
                <p className="text-gray-600 text-sm mt-1 break-words">
                  <FaCommentDots className="inline-block mr-1 text-gray-500" /> {appt.message}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Received: {format(new Date(appt.createdAt), 'MMM dd,yyyy HH:mm')}
                </p>
              </div>
              {appt.status === 'replied' && (
                <FaCheckCircle className="text-green-500 text-2xl" title="Replied" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Appointment Detail Modal/Panel */}
      {selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <h3 className="text-2xl font-bold mb-4 text-gray-800">Appointment Details</h3>
            <div className="mb-4">
              <p className="font-semibold text-gray-700">Name: <span className="font-normal">{selectedAppointment.name}</span></p>
              <p className="font-semibold text-gray-700">Phone: <span className="font-normal">{selectedAppointment.phone}</span></p>
              <p className="font-semibold text-gray-700">Received: <span className="font-normal">{format(new Date(selectedAppointment.createdAt), 'MMM dd,yyyy HH:mm')}</span></p>
              <p className="font-semibold text-gray-700">Status: <span className={`font-normal px-2 py-0.5 rounded ${getStatusColor(selectedAppointment.status)}`}>
                {selectedAppointment.status.toUpperCase()}
              </span></p>
              <p className="font-semibold text-gray-700 mt-2">Message:</p>
              {/* Message in detail modal also uses break-words */}
              <p className="text-gray-800 p-3 bg-gray-50 border rounded break-words">{selectedAppointment.message}</p>
            </div>

            {selectedAppointment.status === 'replied' && selectedAppointment.reply && (
              <div className="mb-4">
                <p className="font-semibold text-gray-700">Your Reply:</p>
                {/* Reply in detail modal also uses break-words */}
                <p className="text-gray-800 p-3 bg-blue-50 border border-blue-200 rounded break-words">{selectedAppointment.reply}</p>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="replyInput" className="block text-gray-700 text-sm font-bold mb-2">
                Send Reply:
              </label>
              <textarea
                id="replyInput"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-cyan-500"
                rows="4"
                placeholder="Type your reply here..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                disabled={selectedAppointment.status === 'replied'} // Disable if already replied
              ></textarea>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeDetails}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition"
              >
                Close
              </button>
              <button
                onClick={handleSendReply}
                className={`px-4 py-2 text-white rounded-md transition
                  ${selectedAppointment.status === 'replied' ? 'bg-gray-400 cursor-not-allowed' : 'bg-cyan-600 hover:bg-cyan-700'}
                `}
                disabled={selectedAppointment.status === 'replied'}
              >
                <FaReply className="inline-block mr-2" /> Send Reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentList;